import { useState } from 'react';
import { useBlackjack } from '../../../hooks/useBlackjack';
import { useWalletStore } from '../../../store/walletStore';
import { HandDisplay } from './HandDisplay';
import type { BlackjackAction } from '../../../api/games';

const ACTION_LABEL: Record<BlackjackAction, string> = {
  hit: 'Hit',
  stand: 'Stand',
  double: 'Double',
  split: 'Split',
  insurance: 'Insurance',
};

export function Blackjack({ minBet, maxBet }: { minBet: number; maxBet: number }) {
  const bj = useBlackjack();
  const balance = useWalletStore((s) => s.balance);
  const [bet, setBet] = useState(minBet || 10);

  const view = bj.view;
  const inRound = bj.roundId !== null && view?.phase === 'player';
  const resolved = view?.phase === 'done';

  const canDeal =
    !bj.busy && !inRound && balance !== null && bet >= minBet && bet <= maxBet && bet <= balance;

  const doAction = (action: BlackjackAction) => {
    const fn = bj[action];
    void fn();
  };

  const resultSummary = () => {
    if (!view?.result) return null;
    const net =
      view.result.totalCredit -
      view.hands.reduce((s, h) => s + h.bet, 0) -
      (view.result.insurance?.bet ?? 0);
    const outcomes = view.result.hands.map((h) => h.outcome).join(', ');
    return (
      <p
        className={`text-center text-lg font-bold ${
          net > 0 ? 'text-emerald-400' : net < 0 ? 'text-rose-400' : 'text-slate-300'
        }`}
      >
        {outcomes} — {net > 0 ? `+${net}` : net} tokens
      </p>
    );
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="card min-h-[220px] space-y-4">
        {view ? (
          <>
            <HandDisplay
              title="Dealer"
              cards={view.dealerCards}
              total={view.dealerTotal}
              hideHole={view.phase === 'player'}
              badge={view.result?.dealerNaturalBlackjack ? 'Blackjack' : undefined}
            />
            <div className="space-y-3">
              {view.hands.map((h, i) => (
                <HandDisplay
                  key={i}
                  title={view.hands.length > 1 ? `Your hand ${i + 1}` : 'Your hand'}
                  cards={h.cards}
                  total={h.total}
                  highlight={view.phase === 'player' && i === view.activeHandIndex}
                  badge={h.blackjack ? 'Blackjack' : h.bust ? 'Bust' : undefined}
                />
              ))}
            </div>
            {resolved && resultSummary()}
          </>
        ) : (
          <p className="py-12 text-center text-slate-400">
            Place your bet and deal to start a hand.
          </p>
        )}
      </div>

      {inRound && view ? (
        <div className="flex flex-wrap justify-center gap-2">
          {view.availableActions.map((a) => (
            <button
              key={a}
              className="btn-gold"
              disabled={bj.busy}
              onClick={() => doAction(a)}
            >
              {ACTION_LABEL[a]}
            </button>
          ))}
        </div>
      ) : (
        <div className="card flex flex-wrap items-center gap-3">
          <label className="label mb-0">Bet</label>
          <input
            type="number"
            className="input w-28"
            min={minBet}
            max={maxBet}
            value={bet}
            onChange={(e) => setBet(Math.floor(Number(e.target.value)))}
          />
          <button className="btn-gold ml-auto" disabled={!canDeal} onClick={() => bj.deal(bet)}>
            {bj.busy ? 'Dealing…' : resolved ? 'Deal again' : 'Deal'}
          </button>
        </div>
      )}

      {bj.error && <p className="text-center text-sm text-rose-400">{bj.error}</p>}
      <p className="text-center text-xs text-slate-500">
        Blackjack pays 3:2 · Dealer stands on 17 · Bet range {minBet}–{maxBet}
      </p>
    </div>
  );
}
